// components/layout/Sidebar.tsx
import React, { useEffect, useState, useRef } from "react";
import { useLocation, Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faChevronLeft,
  // Add default collapsed icon here or accept via prop
} from "@fortawesome/free-solid-svg-icons";
import {
  faTimesCircle,
} from "@fortawesome/free-regular-svg-icons";
// import { ROUTES } from "../../lib/constants"; // Not directly used in Sidebar, paths come from menuItems

// Props for each item in the menuItems array
interface MenuItemProps {
  href: string;
  label: string;
  icon?: React.ReactNode;
  isActiveCheck?: (pathname: string, href: string) => boolean;
}

// A named group of menu items
interface NavSection {
  label: string;
  items: MenuItemProps[];
}

// NavItem Component (defined inside Sidebar file)
interface NavItemComponentProps extends MenuItemProps {
  isTextHidden?: boolean; // True if sidebar is collapsed (desktop) and text should be hidden
  onLinkClick?: () => void; // To handle mobile menu close
}

const NavItem: React.FC<NavItemComponentProps> = ({
  href,
  label,
  icon,
  isActiveCheck,
  isTextHidden,
  onLinkClick,
}) => {
  const location = useLocation();
  const pathname = location.pathname;
  const isActive = isActiveCheck
    ? isActiveCheck(pathname, href)
    : pathname.startsWith(href);

  return (
    <li>
      <Link
        to={href}
        onClick={onLinkClick}
        // Added h-[40px] for consistent height
        className={`flex items-center px-3 py-2.5 text-sm rounded-xl transition-all duration-200 ease-in-out group w-full h-[44px] relative overflow-hidden ${
          isActive
            ? "bg-gradient-to-r from-golden-accent to-golden-accent-hover text-text-on-accent font-semibold shadow-lg shadow-golden-accent/25 scale-105"
            : "text-text-secondary hover:text-text-primary hover:bg-gradient-to-r hover:from-dark-tertiary hover:to-dark-border hover:scale-102"
        } ${isTextHidden ? "justify-center" : ""}`}
        aria-current={isActive ? "page" : undefined}
        title={isTextHidden ? label : undefined}
      >
        {icon && (
          <span
            className={`h-5 w-5 flex-shrink-0 ${
              isActive
                ? "text-text-on-accent"
                : "text-text-secondary group-hover:text-text-primary"
            } ${!isTextHidden ? "mr-3" : "mr-0"}`} // Apply mr-3 only when text is visible
          >
            {icon}
          </span>
        )}
        {!isTextHidden && <span className="truncate">{label}</span>}
      </Link>
    </li>
  );
};

// Props for the main Sidebar component
interface SidebarProps {
  // Sidebar Header
  title: string; // Text title for the header when expanded
  collapsedIcon?: React.ReactNode; // Icon or short text for the header when collapsed
  headerLink?: string; // Link for the header title/icon

  // Navigation Items
  menuItems?: MenuItemProps[]; // Flat list (legacy / superadmin)
  sections?: NavSection[];    // Sectioned list (admin)

  // Mobile state control (passed from parent Layout)
  isOpen?: boolean; // Controls visibility on small screens
  onClose?: () => void; // Callback to close sidebar

  // Optional: Pass function to call on link click (useful for mobile close)
  onLinkClick?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  title,
  collapsedIcon = <span className="text-2xl font-bold">S</span>,
  headerLink = "#",
  menuItems = [],
  sections,
  isOpen = false,
  onClose,
  onLinkClick,
}) => {
  const [isDesktopCollapsed, setIsDesktopCollapsed] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const [showExpandedContent, setShowExpandedContent] = useState(true);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const checkMobileView = () => {
      const mobile = window.innerWidth < 1024; // lg breakpoint
      setIsMobileView(mobile);
      // If resizing from mobile (open) to desktop, ensure mobile overlay state is closed
      if (!mobile && isOpen && onClose) {
        onClose();
      }
    };

    checkMobileView();
    window.addEventListener("resize", checkMobileView);
    return () => {
      window.removeEventListener("resize", checkMobileView);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (isMobileView) {
      // On mobile, expanded content follows mobile menu state
      setShowExpandedContent(isOpen);
    } else {
      // Desktop view
      if (isDesktopCollapsed) {
        // Collapsing: Hide expanded content after a delay
        timeoutRef.current = setTimeout(
          () => setShowExpandedContent(false),
          100
        ); // Shorter delay for content to disappear
      } else {
        // Expanding: Show expanded content immediately
        setShowExpandedContent(true);
      }
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [isDesktopCollapsed, isMobileView, isOpen]);

  const currentTextHiddenState = isMobileView ? !isOpen : !showExpandedContent;

  const toggleDesktopSidebar = () => {
    if (!isMobileView) {
      setIsDesktopCollapsed((prev) => !prev);
    }
  };

  // Dynamically build sidebar classes with enhanced styling
  const sidebarBaseClasses =
    "z-50 bg-gradient-to-b from-dark-secondary via-dark-secondary to-dark-tertiary border-r border-dark-border shadow-2xl flex flex-col transition-all duration-300 ease-in-out backdrop-blur-sm";
  let sidebarDynamicClasses = "";

  if (isMobileView) {
    sidebarDynamicClasses = `fixed h-full top-0 left-0 ${
      isOpen
        ? "w-[200px] translate-x-0"
        : "w-0 -translate-x-full border-transparent overflow-hidden"
    }`;
  } else {
    // Desktop view
    sidebarDynamicClasses = `sticky top-0 h-screen ${
      isDesktopCollapsed ? "w-[72px]" : "w-[200px]"
    }`;
  }

  const handleNavItemClick = () => {
    // Call the parent-provided onLinkClick (if any)
    if (onLinkClick) onLinkClick();
    // Also handle mobile close if needed (redundant if onLinkClick does it, but safe)
    if (isMobileView && isOpen && onClose) {
      onClose();
    }
  };

  return (
    <>
      {/* Overlay for small screens (mobile) when sidebar is open */}
      {isMobileView && isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <aside className={`${sidebarBaseClasses} ${sidebarDynamicClasses}`}>
        {/* Desktop Toggle Button - Refined transitions */}
        {!isMobileView && (
          <button
            onClick={toggleDesktopSidebar}
            className={`
              absolute top-1/2 -translate-y-1/2
              ${isDesktopCollapsed ? " -right-[14px]" : " -right-[14px]"}
              z-[51]
              bg-dark-primary hover:bg-golden-accent
              text-text-secondary hover:text-text-on-accent
              border border-dark-tertiary
              w-7 h-7 flex items-center justify-center
              rounded-full shadow-lg transition-colors duration-200 ease-in-out
              transform ${isDesktopCollapsed ? "rotate-180" : "rotate-0"}
              transition-transform duration-200 ease-in-out
              group
            `}
            aria-label={
              isDesktopCollapsed ? "Expand sidebar" : "Collapse sidebar"
            }
          >
            <FontAwesomeIcon icon={faChevronLeft} className="h-3.5 w-3.5" />
          </button>
        )}

        {/* Enhanced Sidebar Header/Logo */}
        <div
          className={`h-16 flex items-center border-b border-dark-border/50 flex-shrink-0 bg-gradient-to-r from-dark-secondary to-dark-tertiary
            transition-all duration-100 ease-in-out
            ${
              currentTextHiddenState && !isMobileView
                ? "justify-center px-0"
                : "justify-between px-4"
            }`}
        >
          {/* Conditional rendering for logo/title based on showExpandedContent */}
          {showExpandedContent || (isMobileView && isOpen) ? (
            <Link
              to={headerLink}
              className="text-xl font-bold bg-gradient-to-r from-golden-accent to-golden-accent-hover bg-clip-text text-transparent hover:from-golden-accent-hover hover:to-golden-accent transition-all duration-300 truncate"
              onClick={isMobileView && onClose ? onClose : undefined}
            >
              {title}
            </Link>
          ) : (
            !isMobileView && ( // Only show collapsed logo on desktop collapsed
              <Link
                to={headerLink}
                className="text-golden-accent hover:text-golden-accent-hover flex items-center justify-center h-full hover:scale-110 transition-transform duration-200"
                title={title}
              >
                <div className="p-2 rounded-lg bg-golden-accent/10 hover:bg-golden-accent/20 transition-colors duration-200">
                  {collapsedIcon}
                </div>
              </Link>
            )
          )}

          {/* Mobile close button (only shown on mobile when menu is open) */}
          {isMobileView && isOpen && (
            <button
              className="text-text-secondary hover:text-text-primary ml-auto"
              onClick={onClose}
              aria-label="Close menu"
            >
              <FontAwesomeIcon icon={faTimesCircle} size="lg" />
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav
          className={`flex-grow overflow-y-auto overflow-x-hidden transition-all duration-100 ease-in-out
                        ${
                          currentTextHiddenState && !isMobileView
                            ? "px-0" // No horizontal padding when collapsed desktop
                            : "px-3" // Normal horizontal padding when expanded desktop or mobile
                        } py-4`} // Vertical padding remains consistent
        >
          <ul
            className={`space-y-1.5 ${
              currentTextHiddenState && !isMobileView
                ? "flex flex-col items-center"
                : ""
            }`}
          >
            {sections
              ? sections.map((section) => (
                  <li key={section.label}>
                    {/* Section label — hidden when collapsed */}
                    {!currentTextHiddenState && (
                      <p className="px-2 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-widest text-text-secondary/50 select-none">
                        {section.label}
                      </p>
                    )}
                    {currentTextHiddenState && (
                      <div className="my-2 h-px w-8 bg-dark-border/60 mx-auto" />
                    )}
                    <ul className="space-y-1.5">
                      {section.items.map((item) => (
                        <NavItem
                          key={item.href}
                          {...item}
                          isTextHidden={currentTextHiddenState}
                          onLinkClick={handleNavItemClick}
                        />
                      ))}
                    </ul>
                  </li>
                ))
              : menuItems.map((item) => (
                  <NavItem
                    key={item.href}
                    {...item}
                    isTextHidden={currentTextHiddenState}
                    onLinkClick={handleNavItemClick}
                  />
                ))}
          </ul>
        </nav>
      </aside>
    </>
  );
};

export default Sidebar;
